import React from 'react';
import { Star } from 'lucide-react';

interface RainbowEffectProps {
  isRainbow?: boolean;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  intensity?: 'low' | 'medium' | 'high';
}

const RainbowEffect: React.FC<RainbowEffectProps> = ({
  isRainbow = false,
  className = '',
  size = 'medium',
  intensity = 'medium'
}) => {
  if (!isRainbow) return null;

  const colors = ['#FF0000', '#FF7F00', '#FFFF00', '#00FF00', '#0000FF', '#4B0082', '#9400D3'];
  const colorNames = ['red', 'orange', 'yellow', 'green', 'blue', 'indigo', 'purple'];

  const sizeMap = {
    small: 'w-3 h-3',
    medium: 'w-4 h-4',
    large: 'w-6 h-6'
  };

  const particleCount = {
    low: 4,
    medium: 7,
    high: 10
  };

  const starSize = sizeMap[size];
  const count = particleCount[intensity];

  return (
    <div className={`absolute inset-0 pointer-events-none ${className}`}>
      {/* Rainbow stars */}
      {Array.from({ length: count }).map((_, i) => {
        const colorIndex = i % colors.length;
        const color = colors[colorIndex];
        const colorName = colorNames[colorIndex];

        return (
          <div
            key={i}
            className="absolute animate-pulse"
            style={{
              left: `${10 + (i * 15) % 80}%`,
              top: `${10 + (i * 18) % 80}%`,
              animationDelay: `${i * 200}ms`,
              animationDuration: `${1500 + i * 150}ms`
            }}
          >
            <Star
              className={`text-${colorName}-500 ${starSize}`}
              fill={color}
              style={{
                filter: `drop-shadow(0 0 3px ${color})`,
                color: color
              }}
            />
          </div>
        );
      })}

      {/* Rainbow particles */}
      {Array.from({ length: Math.floor(count * 0.7) }).map((_, i) => {
        const colorIndex = (i + 2) % colors.length;
        const color = colors[colorIndex];

        return (
          <div
            key={`particle-${i}`}
            className="absolute animate-ping"
            style={{
              left: `${20 + (i * 25) % 60}%`,
              top: `${20 + (i * 22) % 60}%`,
              animationDelay: `${i * 350 + 500}ms`,
              animationDuration: `${1800 + i * 200}ms`
            }}
          >
            <div
              className="w-1.5 h-1.5 rounded-full"
              style={{
                backgroundColor: color,
                filter: `drop-shadow(0 0 2px ${color})`,
                boxShadow: `0 0 4px ${color}`
              }}
            />
          </div>
        );
      })}

      {/* Rainbow glow aura */}
      <div
        className="absolute inset-0 rounded-full opacity-40"
        style={{
          background: `conic-gradient(
            from 0deg,
            #FF0000 0deg,
            #FF7F00 51.4deg,
            #FFFF00 102.8deg,
            #00FF00 154.2deg,
            #0000FF 205.6deg,
            #4B0082 257deg,
            #9400D3 308.4deg,
            #FF0000 360deg
          )`,
          animation: 'spin 6s linear infinite',
          filter: 'blur(8px)'
        }}
      />

      {/* Floating rainbow sparkles */}
      {Array.from({ length: 5 }).map((_, i) => {
        const colorIndex = (i + 4) % colors.length;
        const color = colors[colorIndex];

        return (
          <div
            key={`sparkle-${i}`}
            className="absolute animate-bounce"
            style={{
              left: `${-5 + (i * 110) % 110}%`,
              top: `${-5 + (i * 75) % 110}%`,
              animationDelay: `${i * 400}ms`,
              animationDuration: `${2500 + i * 300}ms`
            }}
          >
            <div
              className="w-1 h-1 rounded-full"
              style={{
                backgroundColor: color,
                filter: `drop-shadow(0 0 2px ${color})`,
                boxShadow: `0 0 3px ${color}`
              }}
            />
          </div>
        );
      })}
    </div>
  );
};

export default RainbowEffect;
